import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActivityTrackingContext = createContext(null);

// Activity types that can be tracked
export const ActivityType = {
  // Document activities
  DOCUMENT_VIEW: 'document_view',
  DOCUMENT_EDIT: 'document_edit',
  DOCUMENT_UPLOAD: 'document_upload',
  DOCUMENT_DOWNLOAD: 'document_download',
  
  // Case activities
  CASE_VIEW: 'case_view',
  CASE_EDIT: 'case_edit',
  CASE_CREATE: 'case_create',
  
  // Communication activities
  COMMUNICATION_VIEW: 'communication_view',
  COMMUNICATION_LOG: 'communication_log',
  EMAIL_COMPOSE: 'email_compose',
  SMS_COMPOSE: 'sms_compose',
  
  // AI Analysis activities
  AI_EXHIBIT_ANALYSIS: 'ai_exhibit_analysis',
  AI_EXAMINATION_GENERATION: 'ai_examination_generation',
  AI_COMMUNICATION_ANALYSIS: 'ai_communication_analysis',
  AI_BILLING_REVIEW: 'ai_billing_review',
  
  // Billing activities
  TIME_ENTRY_CREATE: 'time_entry_create',
  INVOICE_VIEW: 'invoice_view',
  INVOICE_CREATE: 'invoice_create',
  
  // Other activities
  SEARCH: 'search',
  REPORT_GENERATION: 'report_generation',
  WORKFLOW_EDIT: 'workflow_edit',
  INTAKE_MANAGEMENT: 'intake_management',
  EXAMINATION_PREP: 'examination_prep'
};

export function ActivityTrackingProvider({ children }) {
  const { token, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [activityQueue, setActivityQueue] = useState([]);
  const flushTimeoutRef = useRef(null);
  const lastActivityRef = useRef(null);

  // Load settings on mount
  useEffect(() => {
    if (token) {
      loadSettings();
    }
  }, [token]);

  // Start tracking when settings are loaded and enabled
  useEffect(() => {
    if (settings?.enabled && token) {
      setIsTracking(true);
    } else {
      setIsTracking(false);
    }
  }, [settings, token]);

  // Flush activity queue periodically
  useEffect(() => {
    if (activityQueue.length > 0 && isTracking) {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
      flushTimeoutRef.current = setTimeout(() => {
        flushActivityQueue();
      }, 5000); // Flush every 5 seconds
    }

    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
    };
  }, [activityQueue, isTracking]);

  // End session on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (activityQueue.length > 0) {
        // Try to flush remaining activities synchronously
        navigator.sendBeacon(
          `${API_URL}/api/activity-tracking/log/batch`,
          JSON.stringify(activityQueue)
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activityQueue]);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity-tracking/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading tracking settings:', error);
    }
  };

  const updateSettings = async (updates) => {
    try {
      const params = new URLSearchParams();
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const res = await fetch(`${API_URL}/api/activity-tracking/settings?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        return data;
      }
    } catch (error) {
      console.error('Error updating tracking settings:', error);
    }
    return null;
  };

  const toggleTracking = useCallback(async (enabled) => {
    const result = await updateSettings({ enabled });
    if (result) {
      setIsTracking(enabled);
    }
    return result;
  }, [updateSettings]);

  const flushActivityQueue = async () => {
    if (activityQueue.length === 0 || !token) return;

    const activitiesToFlush = [...activityQueue];
    setActivityQueue([]);

    try {
      await fetch(`${API_URL}/api/activity-tracking/log/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(activitiesToFlush)
      });
    } catch (error) {
      console.error('Error flushing activity queue:', error);
      // Re-add failed activities to queue
      setActivityQueue(prev => [...activitiesToFlush, ...prev]);
    }
  };

  const trackActivity = useCallback((activityType, options = {}) => {
    if (!isTracking || !settings?.enabled) return;

    // Check if this activity type should be tracked based on settings
    const categoryChecks = {
      document: ['document_view', 'document_edit', 'document_upload', 'document_download'],
      case: ['case_view', 'case_edit', 'case_create'],
      communication: ['communication_view', 'communication_log', 'email_compose', 'sms_compose'],
      ai: ['ai_exhibit_analysis', 'ai_examination_generation', 'ai_communication_analysis', 'ai_billing_review'],
      billing: ['time_entry_create', 'invoice_view', 'invoice_create']
    };

    if (categoryChecks.document.includes(activityType) && !settings.track_document_activities) return;
    if (categoryChecks.case.includes(activityType) && !settings.track_case_activities) return;
    if (categoryChecks.communication.includes(activityType) && !settings.track_communication_activities) return;
    if (categoryChecks.ai.includes(activityType) && !settings.track_ai_activities) return;
    if (categoryChecks.billing.includes(activityType) && !settings.track_billing_activities) return;

    const activity = {
      activity_type: activityType,
      case_id: options.caseId || null,
      case_name: options.caseName || null,
      document_id: options.documentId || null,
      document_name: options.documentName || null,
      details: options.details || null,
      metadata: options.metadata || {},
      timestamp: new Date().toISOString()
    };

    lastActivityRef.current = activity;
    setActivityQueue(prev => [...prev, activity]);
  }, [isTracking, settings]);

  // Convenience methods for common activities
  const trackDocumentView = useCallback((caseId, caseName, documentId, documentName) => {
    trackActivity(ActivityType.DOCUMENT_VIEW, { caseId, caseName, documentId, documentName });
  }, [trackActivity]);

  const trackCaseView = useCallback((caseId, caseName) => {
    trackActivity(ActivityType.CASE_VIEW, { caseId, caseName });
  }, [trackActivity]);

  const trackCommunication = useCallback((type, caseId, caseName, details) => {
    trackActivity(type, { caseId, caseName, details });
  }, [trackActivity]);

  const trackAIUsage = useCallback((type, caseId, caseName, details) => {
    trackActivity(type, { caseId, caseName, details });
  }, [trackActivity]);

  // Get billing suggestions
  const getBillingSuggestions = useCallback(async (options = {}) => {
    try {
      const res = await fetch(`${API_URL}/api/activity-tracking/suggest-billing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_date: options.startDate,
          end_date: options.endDate,
          case_ids: options.caseIds,
          minimum_duration_minutes: options.minimumMinutes || 5
        })
      });

      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      console.error('Error getting billing suggestions:', error);
    }
    return null;
  }, [token]);

  // Get activity summary
  const getActivitySummary = useCallback(async (startDate, endDate) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const res = await fetch(`${API_URL}/api/activity-tracking/summary?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      console.error('Error getting activity summary:', error);
    }
    return null;
  }, [token]);

  // Accept billing suggestion
  const acceptSuggestion = useCallback(async (suggestionId, overrides = {}) => {
    try {
      const res = await fetch(`${API_URL}/api/activity-tracking/suggestions/${suggestionId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          suggestion_id: suggestionId,
          ...overrides
        })
      });

      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      console.error('Error accepting suggestion:', error);
    }
    return null;
  }, [token]);

  // Reject billing suggestion
  const rejectSuggestion = useCallback(async (suggestionId) => {
    try {
      const res = await fetch(`${API_URL}/api/activity-tracking/suggestions/${suggestionId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
    }
    return null;
  }, [token]);

  const value = {
    // State
    settings,
    isTracking,
    currentSession,
    
    // Settings
    loadSettings,
    updateSettings,
    toggleTracking,
    
    // Tracking
    trackActivity,
    trackDocumentView,
    trackCaseView,
    trackCommunication,
    trackAIUsage,
    flushActivityQueue,
    
    // Billing suggestions
    getBillingSuggestions,
    getActivitySummary,
    acceptSuggestion,
    rejectSuggestion,
    
    // Activity types
    ActivityType
  };

  return (
    <ActivityTrackingContext.Provider value={value}>
      {children}
    </ActivityTrackingContext.Provider>
  );
}

export function useActivityTracking() {
  const context = useContext(ActivityTrackingContext);
  if (!context) {
    // Return safe defaults when used outside provider or before auth
    return {
      settings: null,
      isTracking: false,
      currentSession: null,
      loadSettings: () => {},
      updateSettings: () => {},
      toggleTracking: () => {},
      trackActivity: () => {},
      trackDocumentView: () => {},
      trackCaseView: () => {},
      trackCommunication: () => {},
      trackAIUsage: () => {},
      flushActivityQueue: () => {},
      getBillingSuggestions: async () => null,
      getActivitySummary: async () => null,
      acceptSuggestion: async () => null,
      rejectSuggestion: async () => null,
      ActivityType
    };
  }
  return context;
}

export default ActivityTrackingContext;
